import React, { useState } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  IconButton,
  Slide,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Share2 as ShareIcon,
  X as XIcon,
  Copy as CopyIcon,
  Mail as MailIcon,
  MessageCircle as SmsIcon,
} from 'lucide-react';

// Brand navy blue color matching the logo
const BRAND_BLUE = '#1e3a8a';

function ShareWidget({ isOpen, onOpen, onClose, hideFab }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.origin;
  const shareTitle = 'Jobs Jobs Jobs - Find Your Dream Job';
  const shareText = 'Check out Jobs Jobs Jobs - an AI-powered job search platform that helps you find and track opportunities!';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent(shareTitle);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleXShare = () => {
    const text = encodeURIComponent(`${shareText} ${shareUrl}`);
    window.open(`https://x.com/intent/tweet?text=${text}`, '_blank', 'width=550,height=420');
  };

  const handleLinkedInShare = () => {
    const url = encodeURIComponent(shareUrl);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=550,height=420');
  };

  const handleSmsShare = () => {
    const body = encodeURIComponent(`${shareText} ${shareUrl}`);
    window.open(`sms:?body=${body}`, '_blank');
  };

  return (
    <>
      {/* FAB - Position 2 (left of Feedback) */}
      {!hideFab && (
        <Fab
          variant="extended"
          onClick={onOpen}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 160,
            zIndex: 1000,
            gap: 1,
            px: 2,
            bgcolor: BRAND_BLUE,
            color: 'white',
            '&:hover': {
              bgcolor: '#172e6e',
            },
          }}
        >
          <ShareIcon size={20} />
          Share
        </Fab>
      )}

      {/* Panel - Same position/size for all widgets */}
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: { xs: 24, sm: 32 },
            right: { xs: 12, sm: 24 },
            left: { xs: 12, sm: 'auto' },
            width: { xs: 'calc(100% - 24px)', sm: 360 },
            maxWidth: { xs: 'calc(100% - 24px)', sm: 360 },
            zIndex: 1200,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: BRAND_BLUE,
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShareIcon size={20} color="white" />
              <Typography variant="h6" fontWeight="bold" color="white">
                Share
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'white' }}
            >
              <XIcon size={20} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Help others discover Jobs Jobs Jobs!
            </Typography>

            {/* Copy Link Button */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CopyIcon size={18} />}
              onClick={handleCopyLink}
              sx={{ mb: 1.5, justifyContent: 'flex-start' }}
            >
              Copy Link
            </Button>

            {/* Native Share (if available) */}
            {navigator.share && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<ShareIcon size={18} />}
                onClick={handleNativeShare}
                sx={{ 
                  mb: 1.5, 
                  justifyContent: 'flex-start',
                  bgcolor: BRAND_BLUE,
                  '&:hover': { bgcolor: '#172e6e' },
                }}
              >
                Share...
              </Button>
            )}

            {/* Social Share Options */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MailIcon size={16} />}
                onClick={handleEmailShare}
                sx={{ flex: '1 1 45%' }}
              >
                Email
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleXShare}
                sx={{ flex: '1 1 45%' }}
              >
                X
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleLinkedInShare}
                sx={{ flex: '1 1 45%' }}
              >
                LinkedIn
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SmsIcon size={16} />}
                onClick={handleSmsShare}
                sx={{ flex: '1 1 45%' }}
              >
                SMS
              </Button>
            </Box>
          </Box>
        </Paper>
      </Slide>

      {/* Copied Snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopied(false)}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ShareWidget;
